import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../../../../context/API';
import EarningsAPI from '../../../../APIs/earningsAPI';
import moment from 'moment';

export interface EarningsRequestParams {
  merchantId: string;
  startDate: moment.Moment | string | null;
  endDate: moment.Moment | string | null;
  store?: string | null;
}

export interface Summary {
  totalRevenue: number;
  totalFees: number;
  netEarnings: number;
  orderCount: number;
  averageOrderValue: number;
}

export interface PlatformBreakdown {
  kiosk: {
    revenue: number;
    fees: number;
    netEarnings: number;
    orderCount: number;
  };
  mobile: {
    revenue: number;
    fees: number;
    netEarnings: number;
    orderCount: number;
  };
}

export interface FulfillmentBreakdown {
  pickup: {
    revenue: number;
    fees: number;
    netEarnings: number;
    orderCount: number;
  };
  delivery: {
    revenue: number;
    fees: number;
    netEarnings: number;
    orderCount: number;
  };
}

export interface FeeCategory {
  amount: number;
  count: number;
  percentage: number;
}

export interface FeeCategories {
  [key: string]: FeeCategory;
}

export interface MerchantData {
  id: string;
  name?: string;
  email?: string;
}

export interface Store {
  id: string;
  name: string;
}

export interface EarningsData {
  merchantId: string;
  merchantName: string;
  period: {
    startDate: string;
    endDate: string;
  };
  summary: Summary;
  platformBreakdown: PlatformBreakdown;
  fulfillmentBreakdown: FulfillmentBreakdown;
  feeCategories: FeeCategories;
}

export interface EarningsApiResponse {
  message: string;
  status: number;
  data: EarningsData;
  success: boolean;
}

interface MerchantEarningsState {
  earningsData: EarningsData | null;
  merchantData: MerchantData | null;
  stores: Store[];
  selectedStore: string | null;
  selectedDateRange: {
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
  };
  loading: boolean;
  initialLoading: boolean;
  error: string | null;
}

const initialState: MerchantEarningsState = {
  earningsData: null,
  merchantData: null,
  stores: [],
  selectedStore: null,
  selectedDateRange: {
    startDate: moment().subtract(1, 'day'),
    endDate: moment(),
  },
  loading: false,
  initialLoading: true,
  error: null,
};

// Fetch merchant profile
export const fetchMerchantData = createAsyncThunk<
  MerchantData,
  void,
  { rejectValue: string }
>('merchantEarnings/fetchMerchantData', async (_, { rejectWithValue }) => {
  const role = localStorage.getItem('role');
  if (role !== 'merchant') {
    return rejectWithValue('Merchant profile is only for merchant role');
  }
  try {
    const token = localStorage.getItem('access_token');
    const response = await API.get('merchant/profile', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch merchant data'
    );
  }
});

// Fetch stores
export const fetchStores = createAsyncThunk<
  Store[],
  { merchantId?: string; role?: string },
  { rejectValue: string }
>('merchantEarnings/fetchStores', async ({ merchantId, role }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    let response;
    
    if (role === 'admin' && merchantId) {
      response = await API.get(`/stores/by-merchant/${merchantId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } else {
      response = await API.get('/stores', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
    
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch stores'
    );
  }
});

// Fetch earnings data
export const fetchEarnings = createAsyncThunk<
  EarningsData,
  EarningsRequestParams,
  { rejectValue: string }
>('merchantEarnings/fetchEarnings', async (params, { rejectWithValue }) => {
  try {
    if (!params.merchantId) {
      return rejectWithValue('Merchant ID is required');
    }

    if (!params.store) {
      return rejectWithValue('Store ID is required');
    }

    const requestParams = {
      startDate: params.startDate,
      endDate: params.endDate,
      merchantId: params.merchantId,
      store: params.store,
    };

    const response = await EarningsAPI.getMerchantEarnings(params.merchantId, requestParams);
    
    if (response && response.data && response.data.data) {
      return response.data.data;
    } else if (response && response.data) {
      return response.data;
    } else {
      return rejectWithValue('No data received from server');
    }
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to fetch earnings data'
    );
  }
});

// Export earnings CSV
export const exportEarningsCSV = createAsyncThunk<
  string,
  EarningsRequestParams,
  { rejectValue: string }
>('merchantEarnings/exportEarningsCSV', async (params, { rejectWithValue }) => {
  try {
    if (!params.merchantId) {
      return rejectWithValue('Merchant ID is required');
    }

    const csvData = await EarningsAPI.exportEarningsCSV(params.merchantId, {
      startDate: params.startDate,
      endDate: params.endDate,
      store: params.store,
    });
    
    return csvData;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to export earnings data'
    );
  }
});

const merchantEarningsSlice = createSlice({
  name: 'merchantEarnings',
  initialState,
  reducers: {
    setSelectedStore: (state, action: PayloadAction<string | null>) => {
      state.selectedStore = action.payload;
    },
    setSelectedDateRange: (
      state,
      action: PayloadAction<{
        startDate: moment.Moment | null;
        endDate: moment.Moment | null;
      }>
    ) => {
      state.selectedDateRange = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearEarningsData: (state) => {
      state.earningsData = null;
    },
    resetState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch merchant data
      .addCase(fetchMerchantData.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchMerchantData.fulfilled, (state, action) => {
        state.merchantData = action.payload;
      })
      .addCase(fetchMerchantData.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch merchant data';
      })
      // Fetch stores
      .addCase(fetchStores.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.stores = action.payload;
        if (action.payload.length > 0 && !state.selectedStore) {
          state.selectedStore = action.payload[0].id;
        }
        state.initialLoading = false;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch stores';
        state.initialLoading = false;
      })
      // Fetch earnings
      .addCase(fetchEarnings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEarnings.fulfilled, (state, action) => {
        state.loading = false;
        state.earningsData = action.payload;
        state.error = null;
      })
      .addCase(fetchEarnings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch earnings data';
        state.earningsData = null;
      })
      // Export CSV
      .addCase(exportEarningsCSV.pending, (state) => {
        state.error = null;
      })
      .addCase(exportEarningsCSV.fulfilled, (state) => {
        // CSV export successful, no state change needed
      })
      .addCase(exportEarningsCSV.rejected, (state, action) => {
        state.error = action.payload || 'Failed to export earnings data';
      });
  },
});

export const {
  setSelectedStore,
  setSelectedDateRange,
  clearError,
  clearEarningsData,
  resetState,
} = merchantEarningsSlice.actions;

export default merchantEarningsSlice.reducer;
